/**
 * @author Vibhor <vibhor.sharma@314ecorp.com>
 * @description RequestEnrollment button
 */
import _ from 'lodash';
import { Button, Modal, Tooltip, Typography } from 'antd';
import { Field } from '@formily/react';
import { Input, Form, FormItem } from '@formily/antd-v5';
import { createForm } from '@formily/core';
import { useEffect, useMemo, useState } from 'react';

import { useAppStoreValues } from 'store/app';
import { usePersistenceStoreActions } from 'store/persistence';
import useUpdateEnrollmentData from 'hooks/admin/mutate/useUpdateEnrollmentData';
import useUpdateProvider from 'hooks/admin/mutate/useUpdateProvider';
import { ELIGIBILITY_PAYER_ENROLLMENT_STATUS_CODES } from 'app-constants/eligibility';
import { NO_ACCESS_TOOLTIP_TEXT } from '@veritable/common/constants';

export interface IProps {
	payer: Payers.IRegisteredPayer;
	providerDetails: Provider.IProviderEnrollmentTableData;
	refetch: VoidFunction;
	isEkycCompleted: boolean;
}

interface IState {
	tinValue: string;
	isModalVisible: boolean;
}

const RequestEnrollment: React.FC<IProps> = ({ payer, providerDetails, refetch, isEkycCompleted }) => {
	const { mutate, isPending } = useUpdateEnrollmentData();
	const { setAllowNavigation } = usePersistenceStoreActions();
	const { canUpdatePayers } = useAppStoreValues().userAccess;
	const updateProvider = useUpdateProvider(true);

	const loading = updateProvider.isPending || isPending;
	const isEnrolled = providerDetails.enrollmentStatus === ELIGIBILITY_PAYER_ENROLLMENT_STATUS_CODES.completed;

	const form = useMemo(() => createForm(), []);
	const initialModel = useMemo<{ tin: string }>(
		() => ({
			tin: providerDetails.tin || '',
		}),
		[providerDetails.tin],
	);

	useEffect(() => {
		form.setInitialValues(initialModel);
	}, [initialModel]);

	const [state, setState] = useState<IState>({
		tinValue: providerDetails.tin,
		isModalVisible: false,
	});

	const resetState = () => {
		form.reset();
		setState({
			tinValue: providerDetails.tin,
			isModalVisible: false,
		});
	};

	const requestEnrollment = () => {
		mutate(
			{
				payerID: payer.id,
				userModel: [
					{
						providerId: providerDetails.providerId,
						enrollmentStatus: ELIGIBILITY_PAYER_ENROLLMENT_STATUS_CODES.requested,
					},
				],
			},
			{
				onSuccess: () => {
					resetState();
					// payer will be added to favorites by BE if it is not already in favorites
					setAllowNavigation(true);
				},
			},
		);
	};

	const submitTin = () => {
		updateProvider.mutate(
			{
				providerId: providerDetails.providerId,
				model: {
					...providerDetails,
					tin: state.tinValue,
				},
			},
			{
				onSuccess: () => {
					requestEnrollment();
					refetch();
				},
			},
		);
	};

	const tinValidation = (tin: string) => _.isNaN(Number(tin)) || tin?.length !== 9;

	return (
		<>
			<Tooltip title={`${canUpdatePayers ? '' : NO_ACCESS_TOOLTIP_TEXT}`}>
				<Button
					type='link'
					loading={loading}
					onClick={() => setState({ ...state, isModalVisible: true })}
					disabled={
						providerDetails.enrollmentStatus === ELIGIBILITY_PAYER_ENROLLMENT_STATUS_CODES.requested ||
						!isEkycCompleted ||
						!canUpdatePayers
					}
				>
					{isEnrolled ? 'Re-enroll' : 'Request Enrollment'}
				</Button>
			</Tooltip>
			<Modal
				open={state.isModalVisible}
				onCancel={resetState}
				onOk={submitTin}
				okText={isEnrolled ? 'Re-enroll' : 'Enroll'}
				closable={false}
				okButtonProps={{ disabled: tinValidation(state.tinValue), loading }}
				title={
					<Typography.Title className='m-0' level={4}>
						{providerDetails.name}
					</Typography.Title>
				}
			>
				<Form form={form} layout='vertical'>
					<Field
						name='tin'
						title='Enter TIN/EIN'
						component={[
							Input,
							{ placeholder: 'Enter 9-digit TIN', onChange: (e) => setState({ ...state, tinValue: e.target.value }) },
						]}
						decorator={[FormItem]}
						validator={[
							{ required: true },
							{
								validator: (value: string) => {
									if (_.isNaN(Number(value)) || (value?.length > 0 && value?.length !== 9)) {
										return 'TIN must be a 9-digit number';
									}
									return null;
								},
							},
						]}
					/>
				</Form>
			</Modal>
		</>
	);
};

export default RequestEnrollment;
